/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { CancelPresentationRounded, ContentCopyOutlined, DeleteRounded, SaveRounded, TableViewOutlined } from '@mui/icons-material';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import ModalDialog from '../../ModalDialog';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import PropTypes from 'prop-types';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import SelectDrillDetails from '../../SelectDrillDetails';
import WellboreVolumeModal from '../WellboreVolumeCalculator';
import { APP_ACTION_TYPES, WELLBORE_DRILL_FLUID } from '../../../../utils/constants';
import WellboreImportModal from '../../../wellboreImportModal';
import { listWellboreMudprofile } from '../../../../store/actions/wellboreImport';
import { COLORS } from '../../../../shared/config/colors';
import { checkPermissions } from '../../../../utils/checkPermissions';

const WellboreModal = ({
  onCloseClick,
  isOpen,
  data,
  setWellbore,
  wellbore,
  setParameterDirty,
  setCheckWellbore,
  properties
}) => {
  const classes = styles();
  const apiRefPipe = useGridApiRef();
  const apiRefAnnular = useGridApiRef();
  const dispatch = useDispatch();
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const mudProfiles = useSelector((state) => state.wellboreMudProfiles);
  const [pipeProperties, setPipeProperties] = useState([]);
  const [annularProperties, setAnnularProperties] = useState([]);

  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges([...pipeProperties, ...annularProperties], onCloseClick);
  const [volumeCalculatorModal, setVolumeCalculatorModal] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const disableAnnular = pipeProperties?.some(item => item?.isDirty === true || item?.isNewRow);
  const disablePipe = annularProperties?.some(item => item?.isDirty === true || item?.isNewRow);

  function generateRandom() {
    const length = 24;
    const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let retVal = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  const handleAddWellbore = (data, setData) => {
    const nextId = data.length;
    const latestTo = data?.length ? data.at(-1)?.to : '';

    const newRow = {
      row: nextId + 1,
      from: latestTo,
      to: '',
      fluid_id: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };

    setData((prev) => [...prev, newRow]);
  };

  useEffect(() => {
    const transformData = (data) =>
      (data || []).map((item, index) => ({
        ...item,
        row: index + 1,
        _id: generateRandom(),
      }));

    setPipeProperties(transformData(wellbore?.pipe));
    setAnnularProperties(transformData(wellbore?.annular));
  }, [wellbore, isOpen]);

  useEffect(() => {
    const handleEditFocus = (data, apiRef) => {
      if (data?.at(-1)?.to === '') {
        const id = data.at(-1)._id;

        setTimeout(() => {
          apiRef.current.scrollToIndexes({ rowIndex: data.length - 1 });
          apiRef.current.setCellMode(id, 'from', 'edit');

          setTimeout(() => {
            apiRef.current.setCellFocus(id, 'from');
          }, 50);
        }, 50);

        apiRef.current.setPage(0);
      }
    };

    handleEditFocus(pipeProperties, apiRefPipe);
    handleEditFocus(annularProperties, apiRefAnnular);
  }, [pipeProperties?.length, annularProperties?.length]);

  const checkWellboreValue = (value) => {
    const row = value;
    const invalidFields = [];
    if (row?.from === undefined || row?.from === null || row?.from === '') invalidFields.push('From MD');
    if (row?.to === undefined || row?.to === null || row?.to === '') invalidFields.push('To MD');
    if (row?.fluid_id === undefined || row?.fluid_id === null || row?.fluid_id === '') invalidFields.push('Fluid');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const handleOpenUploadModal = () => {
    const payload = {
      wellsInfoId: data.wellsInfoId
    };
    dispatch(listWellboreMudprofile(payload, () => {
      setUploadOpen(true);
    }, () => {
      setUploadOpen(false);
    }));
  };

  const validateProperties = (row) => {
    const { from, to, _id } = row;
    if (Number(to) <= Number(from)) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          'To MD should be greater than From MD  value.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  function checkForDuplicates(data) {
    const seen = new Set();

    for (let i = 0; i < data.length; i++) {
      const { from, to, fluid_id } = data[i];
      const key = `${from}|${to}|${fluid_id}}`;

      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [data[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row already exisit',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  }

  const handleSave = (row, key) => {
    const checkData = key === 'pipe' ? pipeProperties : annularProperties;
    if (checkWellboreValue(row)) {
      if (validateProperties(row) && checkForDuplicates(checkData)) {
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        return true;
      }
    }
  };

  const handleImport = (value, importState) => {
    try {
      const selectedProfile = importState === 'Initial State' ? mudProfiles?.data.find((item) => item.runId === value)?.well_init || {} : mudProfiles?.data.find((item) => item.runId === value)?.mud_profile?.profile || {};
      const { drillFluids: originalDrillFLuids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      let hasDeletedWellInitFluid = false;
      const drillFluids = [...originalDrillFLuids, WELLBORE_DRILL_FLUID];
      if (selectedProfile) {
        const units = hydraulics?.drillDetails?.units?.MD || { decimals: 2, label: 'm' };

        const formatFluid = (id) => {
          const fluid = drillFluids.find((item) => item._id === id);
          return fluid
            ? `${fluid.name.trim()} @ ${fluid.MD.toFixed(units.decimals)}${units.label}`
            : '';
        };

        const transformWellInitData = (data) =>
          data.map((detail) => {
            const fluidExists = drillFluids.some(fluid => fluid._id === detail.fluid_id);

            if (!fluidExists) {
              hasDeletedWellInitFluid = true;
            }

            return {
              ...detail,
              _id: generateRandom(),
              from: Number(detail.from),
              to: Number(detail.to),
              fluid_id: formatFluid(detail.fluid_id),
            };
          });

        const transformedPipe = transformWellInitData(selectedProfile?.pipe);
        const transformedAnnular = transformWellInitData(selectedProfile?.annular);

        if (hasDeletedWellInitFluid) {
          setCheckWellbore(true);
          dispatch(
            enqueueSnackbar(
              'Configured Drillfluid in Wellbore Initialization is not found, Please Check',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        } else {
          setCheckWellbore(false);

          const invalidObjects = [...transformedPipe.filter(
            (row) => !checkWellboreValue(row) || !validateProperties(row)
          ), ...transformedAnnular.filter(
            (row) => !checkWellboreValue(row) || !validateProperties(row)
          )];
          if (invalidObjects.length > 0) {
            const invalidMap = {};
            invalidObjects.forEach(obj => {
              invalidMap[obj._id] = true;
            });
            setInvalidRows(invalidMap);

            dispatch(
              enqueueSnackbar(
                'Some rows in the uploaded file have invalid data. Please review them.',
                'error',
                new Date().getTime() + Math.random()
              )
            );
            return;
          }

          if (!checkForDuplicates(transformedPipe) || !checkForDuplicates(transformedAnnular)) {
            dispatch(
              enqueueSnackbar(
                'Duplicate entries found in the uploaded data.',
                'error',
                new Date().getTime() + Math.random()
              )
            );
            return;
          }
          setWellbore({
            pipe: transformedPipe,
            annular: transformedAnnular
          });
          setPipeProperties(transformedPipe);
          setAnnularProperties(transformedAnnular);
          setParameterDirty(prev => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            const updated = [...prev];
            updated[0] = { ...updated[0], isDirty: true };
            return updated;
          });
          dispatch(
            enqueueSnackbar(
              'Imported Successfully!',
              'success',
              new Date().getTime() + Math.random()
            )
          );
          setUploadOpen(false);
        }
      } else {
        setWellbore({
          pipe: [],
          annular: []
        });
      }
    } catch (error) {
      dispatch(
        enqueueSnackbar(
          'Failed to upload.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    }
  };

  const removeWellbore = ({ row, key }) => {
    const dataToFilter = key === 'pipe' ? pipeProperties : annularProperties;
    const setter = key === 'pipe' ? setPipeProperties : setAnnularProperties;
    const updatedWellbore = dataToFilter.filter(item => item._id !== row?._id);
    setter(updatedWellbore);
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], isDirty: true };
      return updated;
    });
    setWellbore(prev => ({
      ...prev,
      [key]: updatedWellbore
    }));
    setDeleteAction(false);
    setDeleteRow({});
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {`${data?.name}`}
        </Grid>
        <Grid item>
          {hydraulics?.details?.data[0]?.hole_depth && hydraulics?.details?.data[0]?.bit_depth
            ? <Tooltip title="Volume Calculator">
              <span>
                <IconButton
                  disabled={properties.some(item => item?.isDirty === true)}
                  onClick={() => {
                    setVolumeCalculatorModal(true);
                  }}
                >
                  <TableViewOutlined fontSize='large' />
                </IconButton>
              </span>
            </Tooltip>
            : null}
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const updateData = (id, field, value, key) => {
    const setter = key === 'pipe' ? setPipeProperties : setAnnularProperties;
    setter(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (key, params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={(id, field, newValue) => updateData(id, field, newValue, key)} />;
  };

  const renderDrillFluidCell = (key, params) => {
    const drillFluids = hydraulics?.drillDetails?.hydraulicsDrillDetails?.drillFluids || [];
    const drillFluidsWithAir = [...drillFluids, WELLBORE_DRILL_FLUID];
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={(id, field, newValue) => updateData(id, field, newValue, key)}
        data={drillFluidsWithAir}
        label={hydraulics?.drillDetails?.units?.MD?.label}
        decimals={hydraulics?.drillDetails?.units?.MD?.decimals}
      />
    );
  };

  const markRowAsSaved = (id, key) => {
    let updated = [];
    const setter = key === 'pipe' ? setPipeProperties : setAnnularProperties;
    setter((prevProperties) => {
      updated = prevProperties.map((item) => {
        if (item._id === id) {
          // eslint-disable-next-line no-unused-vars
          const { isDirty, isNewRow, ...rest } = item;
          return { ...rest };
        }
        return item;
      });
      return updated;
    });
    setTimeout(() => {
      setWellbore(prev => ({
        ...prev,
        [key]: updated
      }));
    }, 0);

    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], isDirty: true };
      return updated;
    });

    setCheckWellbore(false);
  };

  const copyWellbore = (row, key) => {
    const setter = key === 'pipe' ? setPipeProperties : setAnnularProperties;
    setter((prev) => {
      const newRow = {
        ...row,
        row: prev.length + 1,
        _id: generateRandom(),
        isNewRow: true,
        isDirty: true,
      };
      return [...prev, newRow];
    });
  };

  const renderActionCell = (key, params) => {
    const { row } = params;
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || hydraulics?.details?.loading || mudProfiles?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions))}
          onClick={() => {
            if (handleSave(row, key)) {
              setTimeout(() => {
                markRowAsSaved(row._id, key);
              }, 100);
            }
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={hydraulics?.details?.loading || mudProfiles?.loading || row?.isDirty || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions))}
          onClick={() => {
            copyWellbore(row, key);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={hydraulics?.details?.loading || mudProfiles?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow({
              row,
              key
            });
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={onCloseClick}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Grid container item marginLeft='12px' marginTop='15px' marginBottom='15px'>
          <Grid>
            <Typography variant="h5">Wellbore Initialization</Typography>
          </Grid>
          <Box className={classes.customTool} sx={{ opacity: (disablePipe || disableAnnular) ? 0.5 : 1, display: !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions) && 'none !important' }}>
            <Box>
              <label className={classes.importLabel} onClick={() => {
                if (disablePipe || disableAnnular) return;
                if (!mudProfiles?.loading) {
                  handleOpenUploadModal();
                }
              }}>Import</label>
            </Box>
          </Box>
        </Grid>
        <Grid container>
          <Box pl={2}>
            <Typography variant='h5' fontWeight='400'>Pipe
              <Typography sx={{ fontSize: '20px', display: 'inline-block', ml: 1 }} color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Pipe has ${properties[0]?.drillFluidsId}, unless otherwise specified in below)`}</Typography>
            </Typography>
            <Typography sx={{ cursor: 'pointer', display: !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions) && 'none !important' }} variant='button' fontWeight='700' onClick={() => handleAddWellbore(pipeProperties, setPipeProperties)} className={pipeProperties.some(item => item.isDirty === true) || mudProfiles?.loading || disablePipe ? classes.disableIcon : ''}>+ Add</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ opacity: disablePipe ? 0.5 : 1, pointerEvents: disablePipe && 'none' }}>
          <Datagrid
            apiRef={apiRefPipe}
            data={pipeProperties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: '0px'
              },
            }}
            loading={hydraulics?.details?.loading || mudProfiles?.loading}
            columns={columns(renderValueCell, renderActionCell, unit, renderDrillFluidCell, hydraulics?.details?.units?.inputs?.well_init, 'pipe')}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'300px'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            {...(pipeProperties.length <= 100
              ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
              : {})}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={hydraulics?.details?.permissions}
          />
        </Grid>
          <Grid container>
          <Box pl={2}>
             <Typography variant='h5' fontWeight='400'>Annular
                <Typography sx={{ fontSize: '20px', display: 'inline-block', ml: 1 }} color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Annular has ${properties[0]?.drillFluidsId}, unless otherwise specified in below)`}</Typography>
             </Typography>
            <Typography sx={{ cursor: 'pointer', display: !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulics?.details?.permissions) && 'none !important' }} variant='button' fontWeight='700' onClick={() => handleAddWellbore(annularProperties, setAnnularProperties)} className={annularProperties.some(item => item.isDirty === true) || mudProfiles?.loading || disableAnnular ? classes.disableIcon || disableAnnular : ''}>+ Add</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ opacity: disableAnnular ? 0.5 : 1, pointerEvents: disableAnnular && 'none' }}>
          <Datagrid
            apiRef={apiRefAnnular}
            data={annularProperties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: '0px'
              },
            }}
            loading={hydraulics?.details?.loading || mudProfiles?.loading}
            columns={columns(renderValueCell, renderActionCell, unit, renderDrillFluidCell, hydraulics?.details?.units?.inputs?.well_init, 'annular')}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'300px'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            {...(annularProperties.length <= 100
              ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
              : {})}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={hydraulics?.details?.permissions}
          />
        </Grid>
      </Grid>
      {uploadOpen && (
        <WellboreImportModal
          isOpen={uploadOpen}
          data={mudProfiles?.data.filter((item) => item.runId !== data?._id)}
          onCloseClick={() => {
            setUploadOpen(false);
          }}
          handleImportClick={handleImport}
        />
      )}
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeWellbore}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      {
        volumeCalculatorModal && <WellboreVolumeModal
          isOpen={volumeCalculatorModal}
          data={data}
          onCloseClick={() => {
            setVolumeCalculatorModal(false);
          }}
        />
      }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
WellboreModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  setWellbore: PropTypes.any,
  wellbore: PropTypes.any,
  setParameterDirty: PropTypes.any,
  setCheckWellbore: PropTypes.any,
  properties: PropTypes.any
};
WellboreModal.defaultProps = {
  selectedJob: {}
};

export default WellboreModal;
